import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar,
  Clock,
  MapPin,
  Phone,
  XCircle,
  CheckCircle,
  Loader2,
  ArrowLeft
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageAppointmentPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    fetchAppointment();
  }, [token]);

  const fetchAppointment = async () => {
    try {
      const response = await axios.get(`${API_URL}/appointments/manage/${token}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch appointment:', error);
      toast.error('Appointment not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;

    setCancelling(true);
    try {
      await axios.post(`${API_URL}/appointments/cancel/${token}`);
      setCancelled(true);
      toast.success('Appointment cancelled');
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading appointment...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold mb-2">Appointment Not Found</h1>
          <p className="text-muted-foreground mb-4">This link may have expired or the appointment doesn't exist.</p>
          <Link to="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { appointment, business } = data;
  const isCancelled = appointment.status === 'cancelled' || cancelled;
  const isPast = new Date(`${appointment.date}T${appointment.time}`) < new Date();

  return (
    <div className="min-h-screen bg-background" data-testid="manage-appointment-page">
      <div className="max-w-lg mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        <div className="card-base">
          {/* Status Banner */}
          {isCancelled && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg mb-6 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              <span className="font-medium">This appointment has been cancelled</span>
            </div>
          )}

          {appointment.status === 'completed' && (
            <div className="bg-accent/10 text-accent p-4 rounded-lg mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">This appointment has been completed</span>
            </div>
          )}

          {/* Business Info */}
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-extrabold mb-1">{business.name}</h1>
            {business.address && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{business.address}</span>
              </div>
            )}
            {business.phone && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Phone className="w-4 h-4" />
                <span>{business.phone}</span>
              </div>
            )}
          </div>

          {/* Appointment Details */}
          <div className="border-t border-border pt-6">
            <h2 className="font-heading font-bold text-lg mb-4">Appointment Details</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">
                    {new Date(appointment.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                  <div className="text-sm text-muted-foreground">Date</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-muted-foreground mt-0.5" />
                <div>
                  <div className="font-medium">{appointment.time} - {appointment.end_time}</div>
                  <div className="text-sm text-muted-foreground">Time</div>
                </div>
              </div>

              <div className="p-4 bg-secondary/50 rounded-lg">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-heading font-bold">{appointment.service_name}</div>
                    <div className="text-sm text-muted-foreground">Service</div>
                  </div>
                  <div className="text-right">
                    <div className="font-accent font-bold text-lg">${appointment.service_price.toFixed(2)}</div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground mb-1">Customer</div>
                <div className="font-medium">{appointment.customer_name}</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {!isCancelled && !isPast && appointment.status !== 'completed' && (
            <div className="mt-8 pt-6 border-t border-border">
              <Button
                variant="destructive"
                className="w-full"
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="cancel-appointment-btn"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Appointment
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Please cancel at least 24 hours in advance if possible.
              </p>
            </div>
          )}

          {isPast && !isCancelled && appointment.status !== 'completed' && (
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground text-center">
                This appointment date has passed.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageAppointmentPage;
